import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { getApi } from '../../api/bootstrap';
import { Button } from '../../components/Button';
import { PiePagina } from '../../components/PiePagina';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Input } from '../../components/Input';
import { Spinner } from '../../components/Spinner';
import { formatError } from '../../lib/formatError';
import { useAuthStore } from '../../stores/authStore';

const FormSchema = z.object({
  codigo: z
    .string()
    .min(6, 'El código tiene 6 dígitos')
    .max(6, 'El código tiene 6 dígitos')
    .regex(/^\d+$/, 'Sólo dígitos'),
});
type FormData = z.infer<typeof FormSchema>;

interface LocationState {
  otpToken: string;
}

function isLocationState(value: unknown): value is LocationState {
  return (
    typeof value === 'object' &&
    value !== null &&
    'otpToken' in value &&
    typeof value.otpToken === 'string'
  );
}

export function Verify2FAPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: { codigo: '' },
  });

  const mutation = useMutation({
    mutationFn: ({ otpToken, codigo }: { otpToken: string; codigo: string }) =>
      getApi().authClient.verify2FA({ otpToken, codigo }),
    onSuccess: (token) => {
      useAuthStore.getState().setSession({
        token: token.access_token,
        tipoToken: token.tipo,
        locale: token.locale,
        tenantNombre: token.tenant_nombre,
        usuarioNombre: token.usuario_nombre,
      });
      navigate('/mi-simulacion', { replace: true });
    },
  });

  if (!isLocationState(state)) {
    return <Navigate to="/login" replace />;
  }
  const { otpToken } = state;

  return (
    <main className="flex min-h-screen flex-col bg-slate-50 px-4">
      <div className="flex flex-1 items-center justify-center py-6">
        <div className="w-full max-w-sm space-y-6 rounded-2xl bg-white p-6 shadow-sm sm:p-8">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Verificación 2FA</h1>
            <p className="mt-1 text-sm text-slate-500">
              Ingresá el código de tu app de autenticación.
            </p>
          </div>
          <form
            onSubmit={handleSubmit((data) => mutation.mutate({ otpToken, codigo: data.codigo }))}
            className="space-y-4"
            noValidate
          >
            <Input
              label="Código"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              {...register('codigo')}
              error={errors.codigo?.message}
            />
            {mutation.isError && <ErrorMessage>{formatError(mutation.error)}</ErrorMessage>}
            <Button type="submit" disabled={mutation.isPending} className="w-full">
              {mutation.isPending ? <Spinner /> : 'Verificar'}
            </Button>
          </form>
        </div>
      </div>
      <PiePagina />
    </main>
  );
}
